import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FormControl } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { toastr } from "react-redux-toastr";
import moment from "moment";
import {
	FiCheck,
	FiChevronLeft,
	FiChevronRight,
	FiHelpCircle,
	FiMoreHorizontal,
} from "react-icons/fi";
import { useTranslation } from "react-i18next";
import Layout from "components/Layout";
import Loading from "components/Loading";
import Modal from "components/Modal";
import Switch from "components/Switch";
import TimesNavigationTabs from "components/Times/TimesNavigationTabs";
import EmployeeSelectSearch from "components/EmployeeSelectSearch/EmployeeSelectSearch";
import TimeInputWithArrows from "components/TimeInputWithArrows";
import RelativeDateRangeNav from "components/Shared/RelativeDateRangeNav";
import { hideModal, showModal } from "actions/modalActions";
import { getDefaultTimeType } from "actions/timesActions";
import {
	adjustRegistrationEnd,
	filterSummaryEmployees,
	formatSummaryMinutes,
	keepSummaryOrder,
	sortSummaryEmployees,
	summaryShiftBounds,
	isAllowedSummaryPeriod,
} from "components/AttendanceByEmployee/aggregate";
import {
	readSummaryFilters,
	shiftSummaryRange,
	SUMMARY_PRESETS,
	writeSummaryFilters,
} from "components/AttendanceByEmployee/summaryFilters";
import {
	refreshSummaryEmployee,
	reloadSummary,
	useEmployeeSummary,
} from "hooks/attendance/useEmployeeSummary";
import useGroupList from "hooks/attendance/useGroupList";
import { withUbicationId } from "components/TimeRegistrations/timeRegistrationUbication";
import { timesService, ubicationsService } from "services";
import { createApprovalLock } from "utils/approvalInFlight";
import { formatDecimalHoursAsHm } from "components/AttendanceSupervisor/utils/tableUtils";
import { formatLocalizedDate } from "utils/dateConfig";
import {
	computeRelativeDateRange,
	RELATIVE_DATE_PRESETS,
} from "utils/relativeDatePresets";
import "components/AttendanceByEmployee/AttendanceByEmployee.scss";

function employeeRole(employee) {
	return (
		employee?.employee_with_function?.function?.name ||
		employee?.position ||
		employee?.job_title?.name ||
		""
	);
}

function initials(employee) {
	const first = employee?.name?.charAt(0) || "";
	const second = employee?.surname?.charAt(0) || "";
	return `${first}${second}`.toUpperCase();
}

function countClass(kind, value) {
	if (!value) return "summary-count is-clear";
	if (kind === "late" || kind === "early") return "summary-count is-timing";
	return "summary-count is-alert";
}

function isValidClock(value) {
	if (!/^\d{2}:\d{2}$/.test(value || "")) return false;
	const [hours, minutes] = value.split(":").map(Number);
	return hours <= 23 && minutes <= 59;
}

function clockLabel(value) {
	if (value == null || value === "") return "--:--";
	const parsed = moment(value);
	return parsed.isValid() ? parsed.format("HH:mm") : "--:--";
}

function clocksDiffer(requested, received) {
	if (received == null || received === "") return false;
	if (requested == null || requested === "") return true;
	return !moment(requested).isSame(moment(received), "minute");
}

function summaryMenuStyle(button, menu) {
	const rect = button.getBoundingClientRect();
	const menuHeight = menu.offsetHeight || 160;
	const menuWidth = Math.max(menu.offsetWidth || 0, 240);
	const spaceBelow = window.innerHeight - rect.bottom;
	const openUp = spaceBelow < menuHeight + 8;
	const top = openUp ? rect.top - menuHeight - 4 : rect.bottom + 4;
	const left = Math.min(
		Math.max(8, rect.right - menuWidth),
		window.innerWidth - menuWidth - 8
	);
	return {
		position: "fixed",
		top: Math.max(8, Math.min(top, window.innerHeight - menuHeight - 8)),
		left,
		zIndex: 1080,
		minWidth: menuWidth,
	};
}

function atDay(day, clock) {
	return moment(`${day} ${clock}`, "YYYY-MM-DD HH:mm", true);
}

function buildPayload({
	employeeId,
	start,
	end,
	approved,
	typeId,
	ubicationId,
	existing,
	isNew,
}) {
	const adjustedEnd = end ? adjustRegistrationEnd(start, end) : null;
	const base = {
		start: moment(start).utc().format(),
		end: adjustedEnd ? moment(adjustedEnd).utc().format() : null,
		approved: Boolean(approved && start && adjustedEnd),
		type_id: typeId || existing?.type?.id || -1,
		employee_id: Number(employeeId),
		start_source: existing?.start_source || "TIME_REGISTRATIONS",
		end_source: "TIME_REGISTRATIONS",
	};
	if (existing?.start_origin) base.start_origin = existing.start_origin;
	if (existing?.end_origin) base.end_origin = existing.end_origin;
	return withUbicationId(base, ubicationId ?? existing ?? null, { echo: !isNew });
}

function failed(response) {
	return !response || response.ok === false;
}

function AttendanceByEmployee() {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const history = useHistory();
	const defaultTimeType = useSelector((state) => state.timesReducer.default_time_type);
	const [filters, setFilters] = useState(() => readSummaryFilters());
	const [periodError, setPeriodError] = useState(false);
	const [kpi, setKpi] = useState(null);
	const [sort, setSort] = useState({ field: "name", direction: "asc" });
	const [expandedId, setExpandedId] = useState(null);
	const [editing, setEditing] = useState(null);
	const [orderLock, setOrderLock] = useState(null);
	const [ubications, setUbications] = useState([]);
	const [saving, setSaving] = useState(false);
	const approvalLock = useRef(createApprovalLock());
	const editingRef = useRef(null);
	const savingRef = useRef(false);
	const { groups } = useGroupList();
	const { loading, error, employees, totals, page, hasNext, setPage } =
		useEmployeeSummary(filters);

	useEffect(() => {
		writeSummaryFilters(filters);
	}, [filters]);

	useEffect(() => {
		dispatch(getDefaultTimeType());
		ubicationsService
			.getAll()
			.then((response) => {
				if (response?.ok === false) return;
				const list = Array.isArray(response?.data) ? response.data : [];
				setUbications(list);
			})
			.catch(() => setUbications([]));
	}, [dispatch]);

	const viewSignature = [
		filters.startDate,
		filters.endDate,
		filters.employeeIds || "",
		filters.groupId || "",
		filters.ubicationId || "",
		kpi || "",
		sort.field,
		sort.direction,
		page,
	].join("|");

	const visible = useMemo(() => {
		const sorted = sortSummaryEmployees(filterSummaryEmployees(employees, kpi), sort);
		if (!orderLock || orderLock.signature !== viewSignature) return sorted;
		return keepSummaryOrder(employees, orderLock.ids, sorted);
	}, [employees, kpi, sort, orderLock, viewSignature]);

	useEffect(() => {
		setOrderLock((current) =>
			current && current.signature !== viewSignature ? null : current
		);
	}, [viewSignature]);

	const presets = useMemo(
		() => RELATIVE_DATE_PRESETS.filter((preset) => SUMMARY_PRESETS.includes(preset.id)),
		[]
	);

	const showAlert = (message) => {
		dispatch(
			showModal({
				modalProps: {
					open: true,
					title: t("common.error"),
					message,
					closeModal: () => dispatch(hideModal()),
					style: { width: "400px" },
				},
				modalType: "alert",
			})
		);
	};

	const applyRange = (startDate, endDate, preset) => {
		if (!startDate || !endDate) return;
		if (!isAllowedSummaryPeriod(startDate, endDate)) {
			setPeriodError(true);
			return;
		}
		setPeriodError(false);
		setExpandedId(null);
		setFilters((current) => ({
			...current,
			startDate: moment(startDate).format("YYYY-MM-DD"),
			endDate: moment(endDate).format("YYYY-MM-DD"),
			preset: preset || "custom",
		}));
	};

	const updateScope = (patch) => {
		setExpandedId(null);
		setFilters((current) => ({ ...current, ...patch }));
	};

	const toggleSort = (field) => {
		setSort((current) => {
			if (current.field !== field) {
				return { field, direction: field === "name" ? "asc" : "desc" };
			}
			return {
				field,
				direction: current.direction === "asc" ? "desc" : "asc",
			};
		});
	};

	const toggleKpi = (next) => {
		setExpandedId(null);
		setKpi((current) => (current === next ? null : next));
	};

	const updateEditing = (value) => {
		const next = typeof value === "function" ? value(editingRef.current) : value;
		editingRef.current = next;
		setEditing(next);
	};

	const holdOrder = () => {
		setOrderLock({
			signature: viewSignature,
			ids: visible.map((employee) => String(employee.id)),
		});
	};

	const nameFor = (employeeId) =>
		employees.find((item) => String(item.id) === String(employeeId))?.name || "";

	const notifyTimeChange = (row, requestedStart, requestedEnd, response) => {
		const name = nameFor(row.employeeId);
		const day = moment(row.day).format("DD/MM");
		const before = `${clockLabel(row.clockStart)} - ${clockLabel(row.clockEnd)}`;
		const after = `${clockLabel(response?.start ?? requestedStart)} - ${clockLabel(
			response?.end ?? requestedEnd
		)}`;
		const adjusted =
			clocksDiffer(requestedStart, response?.start) ||
			clocksDiffer(requestedEnd, response?.end);
		if (adjusted) {
			toastr.warning(
				t("common.correction"),
				t("times.summary.timeCorrected", { name, day, after })
			);
			return;
		}
		toastr.success(
			t("common.success"),
			t("times.summary.timeUpdated", { name, day, before, after })
		);
	};

	const afterEmployeeChange = async (employeeId) => {
		holdOrder();
		await refreshSummaryEmployee(employeeId);
	};

	const saveTimes = async ({ row, start, end, ubicationId, isNew }) => {
		const payload = buildPayload({
			employeeId: row.employeeId,
			start,
			end,
			approved: isNew ? false : row.approved,
			typeId: isNew ? defaultTimeType?.id : row.registration?.type?.id,
			ubicationId,
			existing: row.registration,
			isNew,
		});
		const response = isNew
			? await timesService.add(payload)
			: await timesService.update(row.registration.id, payload);
		if (failed(response)) {
			showAlert(response?.errors?.[0]?.description || t("common.errorGeneric"));
			return null;
		}
		await afterEmployeeChange(row.employeeId);
		return response;
	};

	const closeAtPlanned = async (row) => {
		const bounds = summaryShiftBounds(row.day, row.plannedStartHm, row.plannedEndHm);
		if (!bounds?.end || !row.clockStart) return;
		setSaving(true);
		try {
			const response = await saveTimes({
				row,
				start: row.clockStart,
				end: bounds.end,
				ubicationId: row.ubication?.id ?? null,
				isNew: false,
			});
			if (response) toastr.success(t("common.success"), t("times.summary.closed"));
		} finally {
			setSaving(false);
		}
	};

	const registerAtPlanned = async (row) => {
		const bounds = summaryShiftBounds(row.day, row.plannedStartHm, row.plannedEndHm);
		if (!bounds) return;
		setSaving(true);
		try {
			const response = await saveTimes({
				row,
				start: bounds.start,
				end: bounds.end,
				ubicationId: row.ubication?.id ?? null,
				isNew: true,
			});
			if (response) toastr.success(t("common.success"), t("times.summary.registered"));
		} finally {
			setSaving(false);
		}
	};

	const captureEdit = () => {
		const draft = editingRef.current;
		return draft ? { ...draft } : null;
	};

	const clearEditIfCurrent = (key) => {
		updateEditing((current) => (current?.key === key ? null : current));
	};

	const saveEdit = async (row, draftSnapshot) => {
		const draft = draftSnapshot || editingRef.current;
		if (!draft || draft.key !== row.key) return;
		if (savingRef.current) return;
		const startClock = isValidClock(draft.start) ? draft.start : "";
		const endClock = draft.end ? draft.end : "";
		if (!startClock || (endClock && !isValidClock(endClock))) {
			showAlert(t("times.summary.invalidTime"));
			return;
		}
		if (!row.registration && !endClock) {
			showAlert(t("times.summary.invalidTime"));
			return;
		}
		const start = atDay(row.day, startClock);
		const end = endClock ? atDay(row.day, endClock) : null;
		if (!start.isValid() || (end && !end.isValid())) {
			showAlert(t("times.summary.invalidTime"));
			return;
		}
		const previousStart = row.clockStart ? row.clockStart.format("HH:mm") : "";
		const previousEnd = row.clockEnd ? row.clockEnd.format("HH:mm") : "";
		const sameUbication = (row.ubication?.id ?? null) === (draft.ubicationId ?? null);
		if (
			row.registration &&
			previousStart === startClock &&
			previousEnd === endClock &&
			sameUbication
		) {
			clearEditIfCurrent(row.key);
			return;
		}
		savingRef.current = true;
		setSaving(true);
		try {
			const response = await saveTimes({
				row,
				start,
				end,
				ubicationId: draft.ubicationId,
				isNew: !row.registration,
			});
			if (!response) return;
			clearEditIfCurrent(row.key);
			if (previousStart === startClock && previousEnd === endClock) {
				toastr.success(
					t("common.success"),
					t("times.summary.ubicationUpdated", {
						name: nameFor(row.employeeId),
						day: moment(row.day).format("DD/MM"),
					})
				);
				return;
			}
			notifyTimeChange(row, start, end, response);
		} finally {
			savingRef.current = false;
			setSaving(false);
		}
	};

	const changeUbication = async (row, ubicationId) => {
		if (!row.registration) {
			updateEditing((current) =>
				current && current.key === row.key
					? { ...current, ubicationId }
					: current
			);
			return;
		}
		setSaving(true);
		try {
			const response = await saveTimes({
				row,
				start: row.clockStart,
				end: row.clockEnd,
				ubicationId,
				isNew: false,
			});
			if (response) {
				toastr.success(
					t("common.success"),
					t("times.summary.ubicationUpdated", {
						name: nameFor(row.employeeId),
						day: moment(row.day).format("DD/MM"),
					})
				);
			}
		} finally {
			setSaving(false);
		}
	};

	const confirmDelete = async (row) => {
		setSaving(true);
		try {
			const response = await timesService.remove(row.registration.id);
			dispatch(hideModal());
			if (failed(response)) {
				showAlert(response?.errors?.[0]?.description || t("common.errorGeneric"));
				return;
			}
			toastr.success(t("common.success"), t("times.registrations.deleteSuccess"));
			await afterEmployeeChange(row.employeeId);
		} finally {
			setSaving(false);
		}
	};

	const askDelete = (row) => {
		const endLabel = row.clockEnd ? row.clockEnd.format("HH:mm") : "--:--";
		dispatch(
			showModal({
				modalProps: {
					open: true,
					title: t("times.registrations.deleteTitle"),
					messageHTML: `<p>${t("times.registrations.deleteConfirmMsg")}</p><p><strong>${moment(row.day).format("DD/MM")}</strong> ${row.clockStart ? row.clockStart.format("HH:mm") : "--:--"} - ${endLabel}</p>`,
					deleteAction: () => confirmDelete(row),
					closeModal: () => dispatch(hideModal()),
					style: { width: "320px" },
				},
				modalType: "delete",
			})
		);
	};

	const approveRows = async (rows, { scope = "all", name = "" } = {}) => {
		const list = rows
			.filter((row) => row.canApprove && !row.approved && row.registration?.id)
			.map((row) => ({ id: row.registration.id, approved: true }));
		if (!list.length) {
			showAlert(t("attendance.noPendingApprove"));
			return;
		}
		if (!approvalLock.current.tryBegin("summary-approve")) return;
		setSaving(true);
		try {
			const response = await timesService.approve(list);
			if (failed(response)) {
				showAlert(response?.errors?.[0]?.description || t("attendance.approveError"));
				return;
			}
			const message =
				scope === "employee"
					? t("times.summary.approvedEmployee", { name })
					: t("times.summary.approvedAll");
			toastr.success(t("common.success"), message);
			holdOrder();
			await reloadSummary({ background: true });
		} finally {
			approvalLock.current.end("summary-approve");
			setSaving(false);
		}
	};

	const toggleApproved = async (row, approved) => {
		if (!row.canApprove) {
			showAlert(t("attendance.approveNeedsStartEnd"));
			return;
		}
		const key = String(row.registration.id);
		if (!approvalLock.current.tryBegin(key)) return;
		setSaving(true);
		try {
			const response = await timesService.approve([
				{ id: row.registration.id, approved },
			]);
			if (failed(response)) {
				showAlert(response?.errors?.[0]?.description || t("attendance.approveError"));
				return;
			}
			if (approved) {
				toastr.success(
					t("common.success"),
					t("times.summary.approvedRow", {
						name: nameFor(row.employeeId),
						day: moment(row.day).format("DD/MM"),
					})
				);
			}
			await afterEmployeeChange(row.employeeId);
		} finally {
			approvalLock.current.end(key);
			setSaving(false);
		}
	};

	const startEdit = (row, focusField = "start") => {
		const field = focusField === "end" ? "end" : "start";
		updateEditing((current) => {
			if (current?.key === row.key) return { ...current, focusField: field };
			return {
				key: row.key,
				start: row.clockStart ? row.clockStart.format("HH:mm") : row.plannedStartHm || "",
				end: row.clockEnd ? row.clockEnd.format("HH:mm") : "",
				ubicationId: row.ubication?.id ?? null,
				focusField: field,
			};
		});
		window.setTimeout(() => {
			document.getElementById(`summary-${field}-${row.key}`)?.focus();
		}, 0);
	};

	const pendingVisible = visible.reduce((sum, employee) => sum + employee.pendingCount, 0);

	return (
		<Layout className="page-times attendance-by-employee" context="times">
			<div className="heading">
				<div className="heading-left">
					<h1 className="title">{t("times.title")}</h1>
				</div>
			</div>
			<TimesNavigationTabs />

			<div className="summary-kpis">
				<KpiCard
					title={t("times.summary.kpiAlerts")}
					hint={
						totals?.periodReady
							? t("times.summary.kpiAlertsHint")
							: t("times.summary.kpiPeriodLoading")
					}
					help={t("times.summary.helpAlerts")}
					pressed={kpi === "alerts"}
					onClick={() => toggleKpi("alerts")}
					value={
						totals?.periodReady
							? `${totals.periodWithAlerts}/${totals.periodStaff}`
							: "—"
					}
				/>
				<KpiCard
					title={t("times.summary.kpiOpen")}
					hint={t("times.summary.kpiOpenHint")}
					help={t("times.summary.helpOpen")}
					pressed={kpi === "open"}
					onClick={() => toggleKpi("open")}
					value={totals ? totals.openEmployees : "—"}
					tone="alert"
				/>
				<KpiCard
					title={t("times.summary.kpiMissing")}
					hint={t("times.summary.kpiMissingHint")}
					help={t("times.summary.helpMissing")}
					pressed={kpi === "missing"}
					onClick={() => toggleKpi("missing")}
					value={
						totals?.periodReady ? totals.periodMissingEmployees : "—"
					}
					tone="alert"
				/>
				<KpiCard
					title={t("times.summary.kpiTiming")}
					hint={t("times.summary.kpiTimingSplit", {
						late: totals?.lateRows || 0,
						early: totals?.earlyRows || 0,
					})}
					help={t("times.summary.helpTiming")}
					pressed={kpi === "timing"}
					onClick={() => toggleKpi("timing")}
					value={totals ? totals.lateRows + totals.earlyRows : "—"}
					tone="timing"
				/>
			</div>

			<div className="summary-toolbar">
				<RelativeDateRangeNav
					startDate={filters.startDate}
					endDate={filters.endDate}
					activeDatePreset={filters.preset}
					presets={presets}
					maxDays={31}
					onDatesChange={({ startDate, endDate }, preset) =>
						applyRange(startDate, endDate, preset)
					}
					onPresetChange={(presetId) => {
						const computed = computeRelativeDateRange(presetId);
						if (computed?.start && computed?.end) {
							applyRange(computed.start, computed.end, presetId);
						}
					}}
					onShift={(direction) => {
						const next = shiftSummaryRange(
							filters.startDate,
							filters.endDate,
							direction
						);
						applyRange(next.startDate, next.endDate, "custom");
					}}
				/>
				<EmployeeSelectSearch
					className="form-select-user mb-0"
					controlId="summaryEmployee"
					multiple={false}
					value={filters.employeeIds || null}
					onChange={(id) => updateScope({ employeeIds: id ? String(id) : "" })}
					placeholder={t("common.searchEmployee")}
					allLabel={t("common.allEmployees")}
				/>
				<FormControl
					as="select"
					className="summary-select"
					aria-label={t("common.filterByGroup")}
					value={filters.groupId}
					onChange={(event) => updateScope({ groupId: event.target.value })}
				>
					<option value="">{t("common.filterByGroup")}</option>
					{groups.map((group) => (
						<option key={group.id} value={group.id}>
							{group.name}
						</option>
					))}
				</FormControl>
				<FormControl
					as="select"
					className="summary-select"
					aria-label={t("times.summary.filterUbication")}
					value={filters.ubicationId}
					onChange={(event) => updateScope({ ubicationId: event.target.value })}
				>
					<option value="">{t("times.summary.filterUbication")}</option>
					{ubications.map((ubication) => (
						<option key={ubication.id} value={ubication.id}>
							{ubication.name}
						</option>
					))}
				</FormControl>
				<button
					type="button"
					className="btn btn-outline-primary summary-approve-all"
					disabled={saving || pendingVisible === 0}
					onClick={() =>
						approveRows(
							visible.flatMap((employee) => employee.rows),
							{ scope: "all" }
						)
					}
				>
					<FiCheck /> {t("times.summary.approvePage")}
				</button>
			</div>
			{periodError && (
				<p className="summary-period-error">{t("times.summary.periodTooLong")}</p>
			)}

			{loading ? (
				<Loading />
			) : error ? (
				<p className="summary-period-error">{t("times.summary.loadError")}</p>
			) : visible.length === 0 ? (
				<p className="summary-empty">{t("times.summary.empty")}</p>
			) : (
				<div className={`summary-table highlight-${kpi || "none"}`}>
					<div className="summary-head">
						<span />
						<SortButton
							label={t("times.summary.employee")}
							active={sort.field === "name"}
							direction={sort.direction}
							onClick={() => toggleSort("name")}
						/>
						<SortButton
							className="col-clocked"
							label={t("times.summary.clocked")}
							active={sort.field === "clocked"}
							direction={sort.direction}
							onClick={() => toggleSort("clocked")}
						/>
						<span className="col-open">{t("times.summary.open")}</span>
						<span className="col-missing">{t("times.summary.missing")}</span>
						<span className="col-late">{t("times.summary.late")}</span>
						<span className="col-early">{t("times.summary.early")}</span>
						<SortButton
							className="col-alerts"
							label={t("times.summary.alerts")}
							active={sort.field === "alerts"}
							direction={sort.direction}
							onClick={() => toggleSort("alerts")}
						/>
						<span>{t("times.summary.hours")}</span>
						<span>{t("times.summary.approval")}</span>
					</div>
					{visible.map((employee) => (
						<EmployeeBlock
							key={employee.id}
							employee={employee}
							expanded={expandedId === employee.id}
							onToggle={() =>
								setExpandedId((current) =>
									current === employee.id ? null : employee.id
								)
							}
							editing={editing}
							setEditing={updateEditing}
							ubications={ubications}
							saving={saving}
							onView={(row) =>
								history.push(
									`/times/${employee.id}/employee?day=${row.day}`
								)
							}
							onClose={closeAtPlanned}
							onRegister={registerAtPlanned}
							onEdit={startEdit}
							onCaptureEdit={captureEdit}
							onSaveEdit={saveEdit}
							onDelete={askDelete}
							onUbication={changeUbication}
							onApprove={toggleApproved}
							onApproveEmployee={() =>
								approveRows(employee.rows, {
									scope: "employee",
									name: employee.name,
								})
							}
							t={t}
						/>
					))}
					<div className="summary-legend">
						<span className="legend-alert">{t("times.summary.legendOpen")}</span>
						<span className="legend-timing">{t("times.summary.legendTiming")}</span>
						<span className="legend-clear">{t("times.summary.legendClear")}</span>
					</div>
				</div>
			)}
			{!loading && !error && (page > 0 || hasNext) && (
				<div className="summary-pager">
					<button
						type="button"
						aria-label={t("times.summary.previousPage")}
						disabled={page === 0}
						onClick={() => {
							setExpandedId(null);
							setPage(page - 1);
						}}
					>
						<FiChevronLeft />
					</button>
					<span>{t("times.summary.pageCurrent", { current: page + 1 })}</span>
					<button
						type="button"
						aria-label={t("times.summary.nextPage")}
						disabled={!hasNext}
						onClick={() => {
							setExpandedId(null);
							setPage(page + 1);
						}}
					>
						<FiChevronRight />
					</button>
				</div>
			)}
			<Modal hideModal={() => dispatch(hideModal())} />
		</Layout>
	);
}

function KpiCard({ title, hint, help, value, pressed, onClick, tone }) {
	return (
		<button
			type="button"
			className={`summary-kpi ${pressed ? "is-pressed" : ""} tone-${tone || "neutral"}`}
			aria-pressed={pressed}
			onClick={onClick}
		>
			<span className="summary-kpi-title">
				{title}
				<FiHelpCircle title={help} aria-label={help} />
			</span>
			<strong className="summary-kpi-value">{value}</strong>
			<small className="summary-kpi-detail">{hint}</small>
		</button>
	);
}

function SortButton({ label, active, direction, onClick, className }) {
	return (
		<button
			type="button"
			className={`summary-sort ${className || ""} ${active ? "is-active" : ""}`}
			onClick={onClick}
		>
			{label}
			<span aria-hidden="true">{active && direction === "desc" ? "↓" : "↑"}</span>
		</button>
	);
}

function EmployeeBlock({
	employee,
	expanded,
	onToggle,
	editing,
	setEditing,
	ubications,
	saving,
	onView,
	onClose,
	onRegister,
	onEdit,
	onCaptureEdit,
	onSaveEdit,
	onDelete,
	onUbication,
	onApprove,
	onApproveEmployee,
	t,
}) {
	const role = employeeRole(employee.employee);
	const approvable = employee.approvedCount + employee.pendingCount;
	const ratio = approvable ? employee.approvedCount / approvable : 0;

	return (
		<div className={`summary-employee ${expanded ? "is-open" : ""}`}>
			<div
				className="summary-row"
				role="button"
				tabIndex={0}
				onClick={onToggle}
				onKeyDown={(event) => {
					if (event.key === "Enter" || event.key === " ") {
						event.preventDefault();
						onToggle();
					}
				}}
			>
				<FiChevronRight className={`summary-chevron ${expanded ? "is-open" : ""}`} />
				<span className="employee-info-wrapper">
					<span className="employee-avatar-wrapper">{initials(employee.employee)}</span>
					<span className="employee-details">
						<span>
							<span className="employee-name-link">{employee.name}</span>
							{employee.employee?.code ? (
								<span className="employee-id"> · ID #{employee.employee.code}</span>
							) : null}
						</span>
						{role ? <span className="summary-role">{role}</span> : null}
					</span>
				</span>
				<span className="col-clocked">
					{employee.clocked}/{employee.planned}
				</span>
				<span className="col-open">
					<span className={countClass("open", employee.open)}>{employee.open}</span>
				</span>
				<span className="col-missing">
					<span className={countClass("missing", employee.missing)}>{employee.missing}</span>
				</span>
				<span className="col-late">
					<span className={countClass("late", employee.late)}>{employee.late}</span>
				</span>
				<span className="col-early">
					<span className={countClass("early", employee.early)}>{employee.early}</span>
				</span>
				<span className="col-alerts">
					<span className={countClass("alerts", employee.reviewCount)}>
						{employee.reviewCount}
					</span>
				</span>
				<span className="summary-hours">
					<strong>{formatSummaryMinutes(employee.workedMinutes)}</strong>
					{employee.plannedHours != null && (
						<small>
							{t("times.summary.plannedHours", {
								hours: formatDecimalHoursAsHm(employee.plannedHours),
							})}
						</small>
					)}
				</span>
				<span
					className="summary-approval"
					onClick={(event) => event.stopPropagation()}
					onKeyDown={(event) => event.stopPropagation()}
				>
					<span className="summary-approval-bar">
						<span style={{ width: `${ratio * 100}%` }} />
					</span>
					<small>
						{t("times.summary.approvedPending", {
							approved: employee.approvedCount,
							pending: employee.pendingCount,
						})}
					</small>
					{employee.pendingCount > 0 && (
						<button
							type="button"
							className="summary-approve-link"
							disabled={saving}
							onClick={onApproveEmployee}
						>
							{t("times.summary.approveEmployee")}
						</button>
					)}
				</span>
			</div>
			{expanded && (
				<div className="summary-detail">
					<h3>
						{t("times.summary.detailTitle", { name: employee.name })}
					</h3>
					<div className="summary-detail-table">
						<div className="summary-detail-head">
							<span>{t("times.summary.ubication")}</span>
							<span>{t("times.summary.day")}</span>
							<span>{t("times.summary.shift")}</span>
							<span>{t("times.summary.clocking")}</span>
							<span>{t("times.summary.hours")}</span>
							<span>{t("times.summary.type")}</span>
							<span>{t("times.summary.approved")}</span>
							<span>{t("times.summary.actions")}</span>
						</div>
						{employee.rows.map((row) => (
							<DetailRow
								key={row.key}
								row={row}
								editing={editing}
								setEditing={setEditing}
								ubications={ubications}
								saving={saving}
								onView={onView}
								onClose={onClose}
								onRegister={onRegister}
								onEdit={onEdit}
								onCaptureEdit={onCaptureEdit}
								onSaveEdit={onSaveEdit}
								onDelete={onDelete}
								onUbication={onUbication}
								onApprove={onApprove}
								t={t}
							/>
						))}
					</div>
				</div>
			)}
		</div>
	);
}

function DetailRow({
	row,
	editing,
	setEditing,
	ubications,
	saving,
	onView,
	onClose,
	onRegister,
	onEdit,
	onCaptureEdit,
	onSaveEdit,
	onDelete,
	onUbication,
	onApprove,
	t,
}) {
	const isEditing = editing?.key === row.key;
	const rowRef = useRef(null);
	const leaveEdit = () => {
		const draft = onCaptureEdit();
		window.setTimeout(() => {
			if (rowRef.current?.contains(document.activeElement)) return;
			onSaveEdit(row, draft);
		}, 0);
	};
	const clockLabel = (value, openEnded) => {
		if (value) return value.format("HH:mm");
		if (openEnded) return "";
		return "—";
	};
	const options = ubications.slice();
	if (row.ubication?.id && !options.some((item) => item.id === row.ubication.id)) {
		options.unshift(row.ubication);
	}
	const typeLabel =
		row.typeKind === "open"
			? t("times.summary.typeOpen")
			: row.typeKind === "missing"
				? t("times.summary.typeMissing")
				: row.typeName || t("times.summary.typeOpen");

	return (
		<div className="summary-detail-row" ref={rowRef}>
			<span>
				<FormControl
					as="select"
					value={
						isEditing
							? editing.ubicationId || ""
							: row.ubication?.id || ""
					}
					disabled={saving}
					onBlur={isEditing ? leaveEdit : undefined}
					aria-label={t("times.summary.ubication")}
					onChange={(event) => {
						const value = event.target.value ? Number(event.target.value) : null;
						if (isEditing) {
							setEditing((current) =>
								current ? { ...current, ubicationId: value } : current
							);
							return;
						}
						onUbication(row, value);
					}}
				>
					<option value="">{t("times.selectUbication")}</option>
					{options.map((ubication) => (
						<option key={ubication.id} value={ubication.id}>
							{ubication.name}
						</option>
					))}
				</FormControl>
			</span>
			<span>{formatLocalizedDate(row.day, "weekdayLong")}</span>
			<span>
				{row.plannedStartHm ? (
					<span className="summary-clock">
						<button
							type="button"
							className="summary-clock-time"
							aria-label={t("times.summary.startTime")}
							disabled={saving}
							onClick={() => onEdit(row, "start")}
						>
							{row.plannedStartHm}
						</button>
						<span aria-hidden="true">-</span>
						<button
							type="button"
							className="summary-clock-time"
							aria-label={t("times.summary.endTime")}
							disabled={saving}
							onClick={() => onEdit(row, "end")}
						>
							{row.plannedEndHm}
						</button>
					</span>
				) : (
					"—"
				)}
			</span>
			<span>
				{isEditing ? (
					<span className="summary-clock-edit">
						<TimeInputWithArrows
							id={`summary-start-${row.key}`}
							aria-label={t("times.summary.startTime")}
							className="summary-time-input"
							value={editing.start}
							placeholder="HH:mm"
							showDecorators={false}
							selectOnFocus
							autoFocus={editing.focusField !== "end"}
							disabled={saving}
							onBlur={leaveEdit}
							onChange={(event) =>
								setEditing((current) =>
									current ? { ...current, start: event.target.value } : current
								)
							}
							onKeyDown={(event) => {
								if (event.key === "Enter") {
									event.preventDefault();
									onSaveEdit(row);
								}
								if (event.key === "Escape") {
									event.preventDefault();
									setEditing(null);
								}
							}}
						/>
						<i>-</i>
						<TimeInputWithArrows
							id={`summary-end-${row.key}`}
							aria-label={t("times.summary.endTime")}
							className="summary-time-input"
							value={editing.end}
							placeholder="HH:mm"
							showDecorators={false}
							selectOnFocus
							autoFocus={editing.focusField === "end"}
							disabled={saving}
							onBlur={leaveEdit}
							onChange={(event) =>
								setEditing((current) =>
									current ? { ...current, end: event.target.value } : current
								)
							}
							onKeyDown={(event) => {
								if (event.key === "Enter") {
									event.preventDefault();
									onSaveEdit(row);
								}
								if (event.key === "Escape") {
									event.preventDefault();
									setEditing(null);
								}
							}}
						/>
					</span>
				) : (
					<span className="summary-clock">
						<button
							type="button"
							className="summary-clock-time"
							aria-label={t("times.summary.startTime")}
							disabled={saving}
							onClick={() => onEdit(row, "start")}
						>
							{clockLabel(row.clockStart)}
						</button>
						<span aria-hidden="true">-</span>
						<button
							type="button"
							className="summary-clock-time"
							aria-label={t("times.summary.endTime")}
							disabled={saving}
							onClick={() => onEdit(row, "end")}
						>
							{clockLabel(
								row.clockEnd,
								row.status === "open" || row.status === "progress"
							)}
						</button>
					</span>
				)}
			</span>
			<span>
				{row.workedMinutes === null ? "—" : formatSummaryMinutes(row.workedMinutes)}
			</span>
			<span>
				<span className={`summary-type type-${row.typeKind}`}>{typeLabel}</span>
			</span>
			<span>
				<Switch
					controlId={`summary-approved-${row.key}`}
					isOn={row.approved}
					disabled={saving || !row.canApprove}
					handleToggle={(event) => onApprove(row, event.target.checked)}
				/>
			</span>
			<span className="summary-row-actions">
				<SummaryRowMenu label={t("times.summary.openActions")} disabled={saving}>
					{(close) => (
						<>
							<button
								type="button"
								className="dropdown-item"
								onClick={() => {
									close();
									onView(row);
								}}
							>
								{t("times.summary.viewDetail")}
							</button>
							{row.canCloseAtPlanned && (
								<button
									type="button"
									className="dropdown-item"
									onClick={() => {
										close();
										onClose(row);
									}}
								>
									{t("times.summary.closePlanned")}
								</button>
							)}
							{row.canRegisterAtPlanned && (
								<button
									type="button"
									className="dropdown-item"
									onClick={() => {
										close();
										onRegister(row);
									}}
								>
									{t("times.summary.registerPlanned")}
								</button>
							)}
							<button
								type="button"
								className="dropdown-item"
								onClick={() => {
									close();
									onEdit(row);
								}}
							>
								{t("times.summary.edit")}
							</button>
							{row.canDelete && (
								<button
									type="button"
									className="dropdown-item text-danger"
									onClick={() => {
										close();
										onDelete(row);
									}}
								>
									{t("times.summary.delete")}
								</button>
							)}
						</>
					)}
				</SummaryRowMenu>
			</span>
		</div>
	);
}

function SummaryRowMenu({ label, disabled, children }) {
	const [open, setOpen] = useState(false);
	const [menuStyle, setMenuStyle] = useState(null);
	const buttonRef = useRef(null);
	const menuRef = useRef(null);

	const place = () => {
		const button = buttonRef.current;
		const menu = menuRef.current;
		if (!button || !menu) return;
		setMenuStyle(summaryMenuStyle(button, menu));
	};

	useLayoutEffect(() => {
		if (!open) return undefined;
		place();
		const onMove = () => place();
		window.addEventListener("scroll", onMove, true);
		window.addEventListener("resize", onMove);
		return () => {
			window.removeEventListener("scroll", onMove, true);
			window.removeEventListener("resize", onMove);
		};
	}, [open]);

	useEffect(() => {
		if (!open) return undefined;
		const onPointer = (event) => {
			if (buttonRef.current?.contains(event.target)) return;
			if (menuRef.current?.contains(event.target)) return;
			setOpen(false);
		};
		const onKey = (event) => {
			if (event.key === "Escape") setOpen(false);
		};
		document.addEventListener("mousedown", onPointer);
		document.addEventListener("keydown", onKey);
		return () => {
			document.removeEventListener("mousedown", onPointer);
			document.removeEventListener("keydown", onKey);
		};
	}, [open]);

	return (
		<>
			<button
				ref={buttonRef}
				type="button"
				className={`btn btn-action dropdown-toggle ${open ? "show" : ""}`}
				aria-label={label}
				aria-expanded={open}
				disabled={disabled}
				onClick={(event) => {
					event.stopPropagation();
					setOpen((current) => !current);
				}}
			>
				<FiMoreHorizontal />
			</button>
			{open &&
				createPortal(
					<div
						ref={menuRef}
						className="dropdown-menu show summary-row-menu"
						style={menuStyle || { position: "fixed", top: -9999, left: 0 }}
						role="menu"
						onMouseDown={(event) => event.preventDefault()}
					>
						{children(() => setOpen(false))}
					</div>,
					document.body
				)}
		</>
	);
}

export default AttendanceByEmployee;
